export const dynamicSort = <T>(
  property: keyof T,
  direction: 'asc' | 'desc' = 'asc'
) => {
  const asc = direction === 'asc'
  return function (a: T, b: T) {
    const aProp =
      typeof a[property] === 'string' ? a[property]?.toLowerCase() : a[property]
    const bProp =
      typeof b[property] === 'string' ? b[property]?.toLowerCase() : b[property]

    return aProp < bProp ? (asc ? -1 : 1) : aProp > bProp ? (asc ? 1 : -1) : 0
  }
}

export const getShortenedList = <T>(data: T[], maxEntries: number): T[] => {
  const results =
    data && data.length > maxEntries
      ? data.slice(0, maxEntries)
      : data
        ? data
        : []

  return results
}

/**
 * This function is used to filter a list of objects based on a string match
 * e.g. filterStringMatcher('test', 'title', 'description') will return true if
 * the object has a title or description that contains the string 'test'
 *
 * It is used as a filter function for the Array.filter() method
 * e.g const filteredList = list.filter(filterStringMatcher('test', 'title', 'description'))
 */
function filterStringMatcher<T>(
  match: string,
  ...fields: Array<keyof T>
): (value: T) => boolean {
  return (value: T) => {
    if (!match) return true

    const search = match.toLowerCase().trim()

    return fields.some((field) => {
      const fieldValue = value[field as keyof T]

      if (typeof fieldValue === 'string') {
        return fieldValue.toLowerCase().includes(search)
      }

      return false
    })
  }
}

export function filterDeeplyNestedStringMatcher<T>(
  match: string,
  ...fields: Array<keyof T | string>
): (value: T) => boolean {
  return (value: T) => {
    if (!match) return true

    const search = match.toLowerCase().trim()

    return fields.some((field) => {
      let fieldValue: T[keyof T] | string | undefined

      if (typeof field === 'string') {
        const nestedFields = field.split('.') as Array<keyof T>
        const nestedValue = nestedFields.reduce(
          // @ts-expect-error object is T and key is keyof T
          (obj, key) => obj?.[key],
          value
        )

        if (typeof nestedValue === 'string') {
          fieldValue = nestedValue.toLowerCase()
        }
        if (typeof nestedValue === 'number') {
          fieldValue = nestedValue.toString()
        }
      } else {
        const valueProp = value[field]
        if (typeof valueProp === 'string') {
          fieldValue = valueProp.toLowerCase()
        }
      }
      if (typeof fieldValue === 'string' && fieldValue.includes(search)) {
        return true
      }

      return false
    })
  }
}
export default filterStringMatcher

export const chunkArray = <T>(
  arr: T[],
  chunkSize: number,
  maxItems = 9
): T[][] =>
  Array.from(
    { length: Math.ceil(Math.min(arr.length, maxItems) / chunkSize) },
    (_, i) => arr.slice(i * chunkSize, i * chunkSize + chunkSize)
  )
